import { PencilIcon } from "@primer/octicons-react";
import React, { useState } from "react";
import { Container, Spinner, Table } from "reactstrap";
import FormModal from "~/components/FormModal";
import TextField from "~/components/TextField";

import useApi from "~/hooks/use-api";

interface EditDescriptorModalProps {
  account: Account | null;
  onSave: () => void;
  onClose: () => void;
}

const EditDescriptorModal = (props: EditDescriptorModalProps) => {
  const { account, onSave, onClose } = props;

  if (!account) return null;

  const { id: accountId, statementDescriptor } = account;

  return (
    <FormModal
      title="Set Descriptor"
      open={Boolean(account)}
      onSave={onSave}
      onClose={onClose}
      path={"/api/stripe/account/" + accountId}
      initValues={{ statementDescriptor }}
      new
    >
      <TextField
        label="Descriptor:"
        placeholder={
          (account && account.statementDescriptor) || "Descriptor..."
        }
        name="statementDescriptor"
        type="text"
      />
    </FormModal>
  );
};

interface Account {
  id: string;
  name: string;
  currency: string;
  tosAccepted: boolean;
  organizationId: string;
  organizationName: string;
  locationId: string | null;
  locationName: string | null;
  /**
   * Payments statement descriptor (visible on customer bank statement)
   */
  statementDescriptor: string | null;
  /**
   * A publicly available phone number to call with support issues.
   */
  supportPhone: string | null;
  /**
   * Account metadata to help identify the LOKE entity this is related to
   */
  metadata: Record<string, string>;
}

export default function StripeAccountsPage() {
  const {
    data: accounts,
    revalidate,
    isValidating,
  } = useApi<Account[]>("/api/stripe/account");
  const [modalAccount, setModalAccount] = useState<Account | null>(null);

  return (
    <Container className="mt-4">
      <h1>Stripe Accounts {isValidating && <Spinner />}</h1>

      <Table>
        <tr>
          <th>ID</th>
          <th>Account</th>
          <th>Organization</th>
          <th>Location</th>
          <th>Descriptor</th>
          <th>Link</th>
        </tr>
        {(accounts || []).map((a) => (
          <tr key={a.id}>
            <td>{a.id}</td>
            <td>{a.name}</td>
            <td>{a.organizationName || a.metadata["Organization Name"]}</td>
            <td>{a.locationName || a.metadata["Location Name"]}</td>
            <td>
              {a.statementDescriptor}{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setModalAccount(a);
                }}
              >
                <PencilIcon size={20} />
              </a>
            </td>
            <td>
              {a.metadata["Operator URL"] && (
                <a href={a.metadata["Operator URL"]}>Open</a>
              )}
            </td>
          </tr>
        ))}
      </Table>
      <EditDescriptorModal
        account={modalAccount}
        onClose={() => setModalAccount(null)}
        onSave={() => {
          revalidate();
          setModalAccount(null);
        }}
      />
    </Container>
  );
}
